import React, { useState, useEffect } from 'react';
import { SignInButton, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Users, BarChart3, Shield, Zap, Star, ChevronLeft, ChevronRight, Menu, X, Globe, Smartphone, TrendingUp, CreditCard, Mail } from 'lucide-react';

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clerkTimeoutReached, setClerkTimeoutReached] = useState(false);
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Redirección automática cuando el usuario está autenticado
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate('/dashboard');
    }
  }, [isSignedIn, isLoaded, navigate]);

  useEffect(() => {
    if (isLoaded) {
      setClerkTimeoutReached(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setClerkTimeoutReached(true);
    }, 8000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoaded]);

  // Mostrar loading mientras Clerk se inicializa
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          {!clerkTimeoutReached ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando...</p>
            </>
          ) : (
            <div className="max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
              <div className="text-gray-900 font-semibold text-lg mb-2">Error de conexión con autenticación</div>
              <div className="text-gray-600 text-sm mb-4">
                No se pudo inicializar Clerk desde esta URL: <span className="font-mono">{window.location.origin}</span>
              </div>
              <div className="text-gray-600 text-sm mb-6">
                Si estás abriendo desde una IP (por ejemplo, 192.168.x.x), Clerk puede bloquear el origen y la app queda en loading.
              </div>
              <button
                type="button"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
                onClick={() => window.location.href = window.location.href}
              >
                Reintentar
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="relative bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img 
                src="https://asset.yooldigital.com/api/assets/bafd2da6-d26c-4f98-9294-0229ac87cbc8/public" 
                alt="Yool Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="https://users.yooldigital.com" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Users
              </a>
              <a href="https://connect.yooldigital.com" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Connect
              </a>
              <a href="https://asset.yooldigital.com" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Asset
              </a>
              <a href="https://email.yooldigital.com" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Email
              </a>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center space-x-4">
              <SignInButton>
                <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  Iniciar Sesión
                </button>
              </SignInButton>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Funcionalidades
                </a>
                <a href="#testimonials" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Testimonios
                </a>
                <a href="#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Precios
                </a>
                <a href="#contact" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Contacto
                </a>
                <div className="pt-4 border-t border-gray-200">
                  <SignInButton mode="redirect">
                    <button
                      className="w-full text-left text-gray-700 hover:text-blue-600 font-medium transition-colors mb-3"
                      onClick={closeMobileMenu}
                    >
                      Iniciar Sesión
                    </button>
                  </SignInButton>
                  <SignInButton mode="redirect">
                    <button
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
                      onClick={closeMobileMenu}
                    >
                      Comenzar Gratis
                    </button>
                  </SignInButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/30 to-indigo-300/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-gradient-to-tr from-purple-200/30 to-pink-300/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-8">
                Centro Administrativo Yool
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
                Panel de{' '}
                <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Control
                </span>{' '}
                Administrativo
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 mb-12 leading-relaxed max-w-2xl">
                Gestiona y supervisa todas las operaciones internas de Yool desde un centro de control unificado y eficiente.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <SignInButton mode="redirect">
                  <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                    Acceder al Panel
                  </button>
                </SignInButton>
                <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-2xl font-semibold text-lg hover:border-blue-600 hover:text-blue-600 transition-all duration-300">
                  Documentación
                </button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 pt-8 border-t border-gray-200">
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-gray-900 mb-1">10K+</div>
                <div className="text-sm text-gray-600">Empresas Activas</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-gray-900 mb-1">99.9%</div>
                <div className="text-sm text-gray-600">Tiempo Activo</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-gray-900 mb-1">24/7</div>
                <div className="text-sm text-gray-600">Soporte Premium</div>
                </div>
              </div>
            </div>
            
            {/* Right Content - Dashboard Mockup */}
            <div className="relative">
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Dashboard Principal</h3>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Primas</span>
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  </div>
                      <div className="text-2xl font-bold text-gray-900">$124,500</div>
                      <div className="text-sm text-[var(--yool-success)]">+12.5%</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Clientes</span>
                    <Users className="h-4 w-4 text-blue-500" />
                  </div>
                      <div className="text-2xl font-bold text-gray-900">2,847</div>
                      <div className="text-sm text-blue-600">+8.2%</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">Rendimiento Mensual</span>
                      <BarChart3 className="h-4 w-4 text-purple-500" />
                    </div>
                    <div className="flex items-end space-x-2 h-20">
                      {[40, 65, 45, 80, 60, 90, 75].map((height, i) => (
                        <div
                          key={i}
                          className="bg-gradient-to-t from-blue-500 to-indigo-500 rounded-t flex-1"
                          style={{ height: `${height}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 animate-bounce">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-gray-700">En línea</span>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">Seguro</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              <Zap className="h-4 w-4 mr-2" />
              Funcionalidades Poderosas
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Módulos administrativos para{' '}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                gestionar Yool
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Herramientas especializadas para administrar todas las operaciones internas de Yool de manera eficiente y centralizada.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-blue-200 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-900 transition-colors">
                  Gestión de Usuarios
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Administra usuarios, roles y permisos del sistema. Control completo sobre accesos, configuraciones y gestión de cuentas internas.
                </p>
                <div className="flex items-center text-blue-600 font-medium text-sm group-hover:text-blue-700">
                  <span>Explorar funcionalidad</span>
                </div>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-green-200 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <BarChart3 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-green-900 transition-colors">
                  Reportes y Métricas
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Genera reportes detallados de operaciones, métricas de rendimiento y análisis de datos internos para toma de decisiones estratégicas.
                </p>
                <div className="flex items-center text-[var(--yool-success)] font-medium text-sm group-hover:text-green-700">
                  <span>Ver demo en vivo</span>
                </div>
              </div>
            </div>
            
            {/* Feature 3 */}
            <div className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-purple-200 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-violet-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-purple-900 transition-colors">
                  Configuración del Sistema
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Configura parámetros globales, ajustes de seguridad, integraciones y personalización de la plataforma según las necesidades de Yool.
                </p>
                <div className="flex items-center text-purple-600 font-medium text-sm group-hover:text-purple-700">
                  <span>Certificaciones</span>
                </div>
              </div>
            </div>
            
            {/* Feature 4 */}
            <div className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-orange-200 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-amber-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-orange-900 transition-colors">
                  Monitoreo y Logs
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Supervisa la actividad del sistema, registros de eventos, auditorías de seguridad y seguimiento de operaciones en tiempo real.
                </p>
                <div className="flex items-center text-orange-600 font-medium text-sm group-hover:text-orange-700">
                  <span>Configurar workflows</span>
                </div>
              </div>
            </div>
            
            {/* Feature 5 */}
            <div className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-indigo-200 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-blue-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-indigo-900 transition-colors">
                  Gestión de Contenido
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Administra contenido, recursos digitales, documentación interna y materiales de la plataforma de manera centralizada.
                </p>
                <div className="flex items-center text-indigo-600 font-medium text-sm group-hover:text-indigo-700">
                  <span>Explorar regiones</span>
                </div>
              </div>
            </div>
            
            {/* Feature 6 */}
            <div className="group relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:border-pink-200 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-50/50 to-rose-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Smartphone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-pink-900 transition-colors">
                  Soporte y Mantenimiento
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Herramientas de soporte técnico, mantenimiento del sistema, gestión de tickets y comunicación con equipos internos.
                </p>
                <div className="flex items-center text-pink-600 font-medium text-sm group-hover:text-pink-700">
                  <span>Descargar app</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              <span>Explorar todas las funcionalidades</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 relative overflow-hidden">

        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium mb-6">
              <Star className="h-4 w-4 mr-2" />
              Testimonios Reales
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Lo que dice nuestro{' '}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                equipo
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              El panel administrativo de Yool facilita la gestión interna y optimiza los procesos operativos de toda la organización.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="group relative bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                  <span className="ml-2 text-sm font-medium text-gray-600">5.0</span>
                </div>
                <blockquote className="text-gray-700 mb-8 leading-relaxed text-lg">
                  "El panel administrativo centraliza todas nuestras operaciones internas. La gestión de usuarios y reportes en tiempo real nos permite mantener un control total sobre la plataforma y optimizar nuestros procesos diarios."
                </blockquote>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    MC
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">María Contreras</div>
                    <div className="text-sm text-gray-600">Directora de Operaciones, Yool</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="group relative bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                  <span className="ml-2 text-sm font-medium text-gray-600">5.0</span>
                </div>
                <blockquote className="text-gray-700 mb-8 leading-relaxed text-lg">
                  "La configuración del sistema y el monitoreo en tiempo real nos permite mantener la plataforma funcionando de manera óptima. Las herramientas de soporte facilitan enormemente nuestro trabajo diario."
                </blockquote>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    JR
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Javier Rodríguez</div>
                    <div className="text-sm text-gray-600">Jefe de Sistemas, Yool</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="group relative bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200/50 hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-violet-50/50 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                  <span className="ml-2 text-sm font-medium text-gray-600">5.0</span>
                </div>
                <blockquote className="text-gray-700 mb-8 leading-relaxed text-lg">
                  "La gestión centralizada de contenido y usuarios nos permite administrar eficientemente todos los aspectos de la plataforma desde un solo lugar. Es una herramienta indispensable."
                </blockquote>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    AS
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Ana Silva</div>
                    <div className="text-sm text-gray-600">Administradora de Plataforma, Yool</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">10,000+</div>
              <div className="text-gray-600">Empresas Activas</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">99.9%</div>
              <div className="text-gray-600">Tiempo de Actividad</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">500M+</div>
              <div className="text-gray-600">Transacciones Procesadas</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-2">150+</div>
              <div className="text-gray-600">Países Atendidos</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 relative overflow-hidden">

        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white/20 text-white rounded-full text-sm font-medium mb-8">
            Comienza Hoy Mismo
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
            ¿Listo para administrar{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
              Yool?
            </span>
          </h2>
          
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 leading-relaxed max-w-3xl mx-auto">
            Accede al panel administrativo y gestiona todas las operaciones internas de Yool desde una plataforma centralizada y eficiente.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <SignInButton>
              <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center">
                Acceder al Panel Administrativo
              </button>
            </SignInButton>
            <button className="border-2 border-white text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300">
              Documentación
            </button>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-white/20">
            <div className="flex flex-col items-center">
              <Shield className="h-8 w-8 text-white mb-2" />
              <span className="text-sm text-blue-100">Seguridad Bancaria</span>
            </div>
            <div className="flex flex-col items-center">
              <CreditCard className="h-8 w-8 text-white mb-2" />
              <span className="text-sm text-blue-100">Sin Tarjeta de Crédito</span>
            </div>
            <div className="flex flex-col items-center">
              <Users className="h-8 w-8 text-white mb-2" />
              <span className="text-sm text-blue-100">Soporte 24/7</span>
            </div>
            <div className="flex flex-col items-center">
              <Globe className="h-8 w-8 text-white mb-2" />
              <span className="text-sm text-blue-100">Disponible Globalmente</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://asset.yooldigital.com/api/assets/bafd2da6-d26c-4f98-9294-0229ac87cbc8/public" 
                  alt="Yool Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Centro de control administrativo para gestionar y supervisar todas las operaciones internas de la plataforma Yool.
              </p>
            </div>
            
            {/* Applications */}
            <div>
              <h3 className="text-lg font-semibold mb-6">Aplicaciones</h3>
              <ul className="space-y-3">
                <li><a href="https://users.yooldigital.com" className="text-gray-400 hover:text-white transition-colors">Users</a></li>
                <li><a href="https://connect.yooldigital.com" className="text-gray-400 hover:text-white transition-colors">Connect</a></li>
                <li><a href="https://asset.yooldigital.com" className="text-gray-400 hover:text-white transition-colors">Asset</a></li>
                <li><a href="https://email.yooldigital.com" className="text-gray-400 hover:text-white transition-colors">Email</a></li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Footer */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2024 Yool. Todos los derechos reservados.
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Términos de Servicio</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">Política de Privacidad</a>
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-green-400" />
                  <span className="text-gray-400">Certificado ISO 27001</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
